/**
 * BrandedDocPane — the "Branded Doc" view mode of `FileContentView`.
 *
 * ── PROVENANCE: THIS IS AN EXTRACTION, NOT A REWRITE ───────────────────────────────────────────
 * Every behaviour here was lifted from `BrandedArtifactCard.tsx` before that file was deleted, and
 * the four pieces the CR named explicitly are all present and unchanged in substance:
 *
 *   1. `GoogleDocsPreview`  — resolves a fresh presigned S3 URL and embeds the REAL DOCX/PPTX in
 *      Google Docs Viewer. Not Office Online, not a content mimic.
 *   2. `triggerRegen`       — re-runs the branded pipeline from the latest saved markdown, then
 *      invalidates the preview caches so the iframe refetches. A second trigger supersedes the
 *      first via `AbortController`.
 *   3. `cancelRegen`        — client-side abort only. See the limitation note on the function.
 *   4. `handleDownload`     — fresh presigned URL → synthetic anchor click (in the parent).
 *
 * `isRegenerating` is still reported upward (`onRegenerateStateChange`) so `FileContentView` can
 * disable "Submit for Commit" — committing mid-regeneration would submit stale content.
 *
 * ── WHAT CHANGED, AND WHY IT HAD TO ────────────────────────────────────────────────────────────
 * The extraction also carried over two defects, both now fixed here:
 *
 *   • `onRegenerateRef` is GONE. This pane used to hand `triggerRegen` upward through a ref so an
 *     inline markdown save could re-run the pipeline. But this pane renders only while it is the
 *     active view mode, so the ref was null for any user who had never opened the Branded tab, and
 *     the parent's `regenRef.current?.()` silently did nothing. Regeneration now lives in
 *     `useRegenerateBrandedDoc`, which every caller reaches without this component existing.
 *   • The preview no longer fetches through a mutation behind a mount-once `loadedFor` ref. That
 *     ref is why a SUCCESSFUL regenerate still showed the old document: there was no cache entry
 *     to invalidate and no second fetch. `useBrandedDocUrl` makes the URL a keyed query, so the
 *     invalidation performed by `useRegenerateBrandedDoc` actually refreshes what is on screen.
 *
 * ── ONE STALE CLAIM CORRECTED ──────────────────────────────────────────────────────────────────
 * `BrandedArtifactCard`'s docstring claimed `kickoff_deck` rendered through `KickoffSlidePreview`
 * and `preliminary_srs` through a server-rendered HTML iframe. Neither was true of the code as it
 * stood — both types went through Google Docs Viewer, and `KickoffSlidePreview`,
 * `useFetchSrsHtmlPreview` and `useFetchPptxMarkdown` had no consumers at all. The extraction
 * preserves the code's actual behaviour, not the docstring's. Those three remain unconsumed; see the
 * handover note, because reviving them is a behaviour change nobody has specified.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, RefreshCw, XCircle } from "lucide-react";
import { toast } from "sonner";

import {
  isNotProjectMemberError,
  toastIfNotProjectMember,
} from "@/lib/project-permissions";

import { Button } from "@/components/ui/button";
import {
  useBrandedDocUrl,
  useRegenerateBrandedDoc,
} from "@/hooks/useArtifacts";
import {
  BRANDED_TYPES,
  DOWNLOAD_FILENAMES,
  brandedPipelineFor,
  isBrandedType,
  type BrandedArtifactType,
} from "@/lib/branded-docs";

// Re-exported so existing import sites (`FileContentView`) keep working after the helpers moved to
// `lib/branded-docs` — they had to move so `useArtifacts` could use them without a cycle.
export {
  BRANDED_TYPES,
  DOWNLOAD_FILENAMES,
  brandedPipelineFor,
  isBrandedType,
  type BrandedArtifactType,
};

// ── Google Docs Viewer ──────────────────────────────────────────────────────────────────────────
// Resolves a presigned S3 URL (cache-keyed, so a regeneration can refresh it), then embeds the
// actual file in Google Docs Viewer. No Office Online, no auth issues, no content mimic.

interface GoogleDocsPreviewProps {
  projectId: string;
  brandedType: "srs" | "pptx";
  active: boolean;
}

function GoogleDocsPreview({
  projectId,
  brandedType,
  active,
}: GoogleDocsPreviewProps): JSX.Element {
  const {
    data: downloadUrl,
    isPending,
    isError,
  } = useBrandedDocUrl(projectId, brandedType, active);

  // While a refetch is in flight after a regeneration the PREVIOUS url is still rendered, which is
  // what we want — no flash of empty state. The iframe swaps the moment the new url resolves,
  // because it is keyed on the url and every regeneration returns a fresh presigned one.
  const viewerUrl = downloadUrl
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl)}&embedded=true`
    : null;

  if (isPending && !isError) {
    return (
      <div
        className="flex h-[700px] items-center justify-center gap-2 text-sm text-text-secondary"
        role="status"
        aria-label="Loading branded document preview"
      >
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        Loading preview…
      </div>
    );
  }

  if (isError || !viewerUrl) {
    return (
      <div className="flex h-[400px] flex-col items-center justify-center gap-3 text-text-secondary">
        <p className="text-sm">
          {isError
            ? // "generate it first" used to point at Phase0Page's header Generate button, removed
              // by the 2026-10-03 fix batch (Item 2 — generation now starts from the chat prompt).
              //
              // THIS pane's own `Regenerate` button, named below, is RETAINED and is a DIFFERENT
              // operation that shares only the word (§3.5.1): it re-runs the branded-doc RENDER over
              // the artifact's existing markdown. No Bedrock call, no write to
              // `artifact_edits.content`, scoped to one artifact's rendered output. It is the only
              // way to recover from a failed branded render, so removing it would strand this pane.
              "Document not ready yet — click Regenerate below to re-render it."
            : "Loading…"}
        </p>
      </div>
    );
  }

  return (
    <iframe
      key={viewerUrl}
      src={viewerUrl}
      className="h-[720px] w-full rounded-lg border border-outlines"
      title={brandedType === "srs" ? "SRS Document" : "Kickoff Deck"}
      allowFullScreen
    />
  );
}

export interface BrandedDocPaneProps {
  projectId: string;
  artifactType: BrandedArtifactType;
  /** True while this pane is the visible view mode — gates the presigned-URL fetch. */
  active: boolean;
  /** Reports regeneration state upward so the parent can gate Submit for Commit. */
  onRegenerateStateChange?: (isRegenerating: boolean) => void;
  /**
   * Whether the viewer may regenerate (POST branded-docs/download) — a project member, or
   * leadership/admin. False hides the Regenerate control; the preview stays. Defaults to true;
   * `FileContentView` passes the real gate from `Phase0Page`.
   */
  canRegenerate?: boolean;
}

export function BrandedDocPane({
  projectId,
  artifactType,
  active,
  onRegenerateStateChange,
  canRegenerate = true,
}: BrandedDocPaneProps): JSX.Element {
  const brandedType = brandedPipelineFor(artifactType);
  const { mutateAsync: regenerateBranded } = useRegenerateBrandedDoc(projectId);

  const [regenState, setRegenState] = useState<"idle" | "regenerating">("idle");
  const isRegenerating = regenState === "regenerating";

  /** Abort handle for the in-flight regeneration request — see `cancelRegen` for the caveat. */
  const regenAbortRef = useRef<AbortController | null>(null);

  // Abort any in-flight regeneration if the pane unmounts (view-mode switch / tab close).
  useEffect(() => {
    return () => regenAbortRef.current?.abort();
  }, []);

  useEffect(() => {
    onRegenerateStateChange?.(isRegenerating);
  }, [isRegenerating, onRegenerateStateChange]);

  // On unmount (e.g. switching to Edit / MD Preview mid-regeneration) the in-flight request is
  // aborted above, so tell the parent regeneration is OVER. Without this the parent's flag stayed
  // `true` forever — "Submit for Commit" was stuck on "Regenerating — please wait" because the
  // parent stays mounted across a view-mode change and nothing else ever cleared it.
  const reportRef = useRef(onRegenerateStateChange);
  reportRef.current = onRegenerateStateChange;
  useEffect(() => () => reportRef.current?.(false), []);

  /**
   * Regenerate the branded doc from the latest saved markdown. The cache invalidation that makes
   * the visible iframe refetch lives in `useRegenerateBrandedDoc` — shared with the inline-save and
   * chat-accept paths, so all three refresh identically rather than each inventing its own.
   *
   * While this runs, `regenState` is 'regenerating' — which disables Submit for Commit in the parent
   * (committing mid-regeneration would submit stale content) and reveals the Cancel control.
   */
  const triggerRegen = useCallback(async (): Promise<void> => {
    // A second trigger supersedes the first — abort the previous request.
    regenAbortRef.current?.abort();
    const controller = new AbortController();
    regenAbortRef.current = controller;

    setRegenState("regenerating");
    const toastId = toast.loading("Regenerating branded doc…");
    try {
      await regenerateBranded({ artifactType, signal: controller.signal });
      toast.success("Branded doc updated", { id: toastId });
    } catch (err) {
      // An abort is a user action, not a failure.
      if (controller.signal.aborted) {
        toast.info(
          "Regeneration cancelled — the document may still update in the background",
          {
            id: toastId,
          },
        );
      } else if (isNotProjectMemberError(err)) {
        // The server's "Only members of this project can …" — shown once, under the shared id.
        toast.dismiss(toastId);
        toastIfNotProjectMember(err);
      } else {
        toast.error("Could not regenerate branded doc", { id: toastId });
      }
    } finally {
      // Only the newest request owns the UI state.
      if (regenAbortRef.current === controller) {
        regenAbortRef.current = null;
        setRegenState("idle");
      }
    }
  }, [regenerateBranded, artifactType]);

  /**
   * Cancel an in-flight regeneration.
   *
   * LIMITATION — this is a client-side abort only. Branded-doc regeneration is a synchronous
   * `POST .../branded-docs/download?type=…`, not a tracked job, so there is no server-side cancel
   * endpoint to call (unlike Phase 0 generation, which has `POST /phase0/stop`). Aborting stops the
   * client waiting and restores the UI (re-enabling Submit for Commit); the server may still finish
   * regenerating the document. The user is told this in the toast and the button tooltip.
   */
  const cancelRegen = useCallback((): void => {
    regenAbortRef.current?.abort();
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <GoogleDocsPreview
        projectId={projectId}
        brandedType={brandedType}
        active={active}
      />

      {canRegenerate && (
        <div className="flex justify-end gap-2">
          {isRegenerating && (
            <Button
              variant="outline"
              size="sm"
              onClick={cancelRegen}
              title="Stop waiting for this regeneration. The document may still finish updating on the server."
            >
              <XCircle className="mr-1 h-4 w-4" aria-hidden="true" />
              Cancel
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => void triggerRegen()}
            disabled={isRegenerating}
          >
            {isRegenerating ? (
              <Loader2
                className="mr-1 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            ) : (
              <RefreshCw className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            {isRegenerating ? "Regenerating…" : "Regenerate"}
          </Button>
        </div>
      )}
    </div>
  );
}

export default BrandedDocPane;
